import { fetchCategoryLevelData } from './dataApi';
import { mergeStoriesWithNative } from './storyLocalization';

const LEVELS = ['easy', 'medium', 'difficult'];

const fetchStoriesLevelData = (level, path, options) =>
  fetchCategoryLevelData('stories', level, path, options);

// Stories are organized per difficulty level (easy/medium/difficult — see
// stories/{level}/catalog.json), each with a handful of stories at
// stories/{level}/story{N}.json — an English-anchored root (wrapped as
// { story: {...} }) plus stories/{level}/native/{code}/story{N}.json
// overlays. A locale's overlay may be a full re-authored story (id/title/
// level/emoji/content) or just the reduced pair (id/title/content) — only a
// story's title/content are ever read from a non-English locale, so that's
// all it needs. Unlike conversationV2.js, a locale's own doc is returned
// as-is (no merge onto the English root): a reduced locale simply omits
// level/emoji, and callers already default those client-side (see
// StoriesPage.jsx's LEVEL_META fallback) — matching the
// single-document-per-locale behavior this replaced.

// "story_{id}" -> { level, path }, built once from every level's
// catalog.json.
let catalogIndexPromise = null;

async function fetchCatalogIndex() {
  if (!catalogIndexPromise) {
    catalogIndexPromise = (async () => {
      const index = new Map();
      for (const level of LEVELS) {
        try {
          const catalog = await fetchStoriesLevelData(level, 'catalog.json');
          for (const entry of Object.values(catalog)) {
            if (!entry?.id || !entry?.path) continue;
            index.set(entry.id, { level, path: entry.path });
          }
        } catch {
          // This level's catalog isn't reachable yet — skip it rather than
          // losing every other level that did load.
        }
      }
      return index;
    })();
  }
  return catalogIndexPromise;
}

// title/content are authored as { txt, translate, translationHint } (same
// convention as topic-lessons/exam/conversation), so unwrap once here
// rather than touching every call site (mergeStoriesWithNative,
// StoriesPage.jsx, ...).
function unwrapText(value) {
  return value && typeof value === 'object' && 'txt' in value ? value.txt ?? null : value;
}

function unwrapStory(story) {
  return { ...story, title: unwrapText(story.title), content: unwrapText(story.content) };
}

async function fetchLocaleStories(code) {
  const catalogIndex = await fetchCatalogIndex();
  const isEnglish = !code || code === 'en';
  const stories = [];
  for (const { level, path } of catalogIndex.values()) {
    try {
      const doc = isEnglish
        ? await fetchStoriesLevelData(level, path)
        : await fetchStoriesLevelData(level, `native/${code}/${path}`);
      stories.push(unwrapStory(doc.story));
    } catch {
      // No content for this story in this locale yet — skip it rather than
      // failing the whole list.
    }
  }
  return { stories };
}

export function loadStories(homeLang) {
  return fetchLocaleStories(homeLang);
}

export function loadStoriesNativeOverlay(homeLang, code) {
  return fetchLocaleStories(code).catch(() => ({ stories: [] }));
}

export async function loadStoryPack(homeLang, nativeCode) {
  const targetDoc = await loadStories(homeLang);
  const nativeStories = nativeCode === homeLang
    ? targetDoc.stories
    : (await loadStoriesNativeOverlay(homeLang, nativeCode)).stories;
  return mergeStoriesWithNative(targetDoc.stories, nativeStories);
}

export function clearStoriesCache() {
  catalogIndexPromise = null;
}
